import { useCallback, useMemo } from 'react'
import { useBookingSystem } from '@/contexts/BookingSystemContext'
import { iosCreateBookingCalendarEvent, iosDeleteBookingCalendarEvent } from '@/lib/iosAvailabilitySync'

export type CalendarProvider = 'google' | 'ios' | 'none'

export interface CalendarEventData {
  clientUsername: string
  shortSummary: string
  scheduledTimestamp: Date
  estimatedTime?: number // in hours
}

export interface CalendarSyncResult {
  provider: CalendarProvider
  eventId: string | null
}

/**
 * Unified calendar sync hook that abstracts Google Calendar (backend) and iOS Calendar (native).
 * 
 * - Google Calendar events are created by the backend during booking creation
 * - iOS Calendar events are created on-device via Capacitor plugin
 * 
 * The hook automatically detects which calendar is connected and uses the appropriate method.
 * If both are connected (shouldn't happen), iOS takes priority since it's on-device.
 */
export const useCalendarSync = () => {
  const { googleCalendar, iosCalendar } = useBookingSystem()

  // Memoize the active provider to prevent recalculation on every render
  const activeProvider = useMemo((): CalendarProvider => {
    // iOS takes priority if both are somehow connected (edge case)
    if (iosCalendar.isConnected && iosCalendar.hasPermission) {
      return 'ios'
    }
    if (googleCalendar.isConnected) {
      return 'google'
    }
    return 'none'
  }, [googleCalendar.isConnected, iosCalendar.isConnected, iosCalendar.hasPermission])

  /**
   * Create a calendar event for a booking.
   * 
   * - Google Calendar: Events are created server-side by the backend
   * - iOS Calendar: Events are created on-device here
   * 
   * Returns the iOS event ID if created, or null for Google (handled by backend)
   */
  const createBookingEvent = useCallback(
    async (data: CalendarEventData): Promise<CalendarSyncResult> => {
      if (activeProvider === 'none') {
        console.log('[CalendarSync] No calendar connected, skipping event creation')
        return { provider: 'none', eventId: null }
      }

      if (activeProvider === 'google') {
        // Google Calendar events are created by the backend
        // The backend stores googleCalendarEventId in the booking document
        console.log('[CalendarSync] Google Calendar active - event will be created by backend')
        return { provider: 'google', eventId: null }
      }

      if (activeProvider === 'ios') {
        try {
          console.log('[CalendarSync] Creating iOS Calendar event')
          const eventId = await iosCreateBookingCalendarEvent({
            clientUsername: data.clientUsername,
            shortSummary: data.shortSummary,
            scheduledTimestamp: data.scheduledTimestamp,
            estimatedTime: data.estimatedTime ? data.estimatedTime * 60 : 60 // Convert hours to minutes
          })
          console.log('[CalendarSync] iOS Calendar event created:', eventId)
          return { provider: 'ios', eventId }
        } catch (error) {
          console.error('[CalendarSync] Error creating iOS Calendar event:', error)
          // Don't throw - allow booking to proceed even if calendar fails
          return { provider: 'ios', eventId: null }
        }
      }

      return { provider: 'none', eventId: null }
    },
    [activeProvider]
  )

  /**
   * Delete a calendar event when a booking is cancelled.
   * 
   * - Google Calendar: Deletion is handled server-side by the backend
   * - iOS Calendar: Deletion is done on-device here
   */
  const deleteBookingEvent = useCallback(
    async (eventIds: { googleCalendarEventId?: string; iosCalendarEventId?: string }): Promise<void> => {
      // iOS Calendar deletion (on-device)
      if (eventIds.iosCalendarEventId && (activeProvider === 'ios' || iosCalendar.isConnected)) {
        try {
          console.log('[CalendarSync] Deleting iOS Calendar event:', eventIds.iosCalendarEventId)
          await iosDeleteBookingCalendarEvent(eventIds.iosCalendarEventId)
          console.log('[CalendarSync] iOS Calendar event deleted')
        } catch (error) {
          console.error('[CalendarSync] Error deleting iOS Calendar event:', error)
          // Don't throw - allow cancellation to proceed
        }
      }

      // Google Calendar deletion is handled by the backend
      if (eventIds.googleCalendarEventId) {
        console.log('[CalendarSync] Google Calendar event will be deleted by backend')
      }
    },
    [activeProvider, iosCalendar.isConnected]
  )

  // Memoize derived boolean values to prevent unnecessary re-renders
  const isCalendarConnected = activeProvider !== 'none'
  const isGoogleCalendar = activeProvider === 'google'
  const isIosCalendar = activeProvider === 'ios'

  return {
    /** The currently active calendar provider */
    activeProvider,
    /** Whether any calendar is connected */
    isCalendarConnected,
    /** Whether Google Calendar is the active provider */
    isGoogleCalendar,
    /** Whether iOS Calendar is the active provider */
    isIosCalendar,
    /** Create a calendar event for a booking (iOS only - Google is backend) */
    createBookingEvent,
    /** Delete a calendar event when cancelling (iOS only - Google is backend) */
    deleteBookingEvent
  }
}

